import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Users, FileText, Receipt, BarChart3, Settings, LogOut, KeyRound, Menu, X, ClipboardCheck, Banknote, Wallet, Sparkles, HelpCircle, Sun, Moon, AlertTriangle, FileBadge2, UserCog, Tag, UsersRound, Boxes } from "lucide-react";
import { useEffect, useState } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import api from "../lib/api";
import { applyBrandingVars, displayName, initials } from "../lib/branding";
import { getInitialTheme, applyTheme } from "../lib/theme";
import { alertaVencidaTexto } from "../lib/financeiro";
import { PLATFORM_COMPANY_NAME } from "../lib/platformCompany";
import ChangePasswordDialog from "./ChangePasswordDialog";

// Pacote consolidado pós-homologação, Prioridade 14 — "Enquanto bloqueado:
// esconder/bloquear Dashboard, Clientes, Fichas, Aprovações, Faturamento,
// Relatórios, demais módulos". O backend já bloqueia de verdade (rotas
// operacionais retornam 403 — ver FINANCIAL_BLOCK_ALLOWED_PATHS/PREFIXES
// em server.py); aqui é só a experiência: nem mostrar um link que levaria
// a uma tela redirecionada de volta pelo ProtectedRoute.
// Experimento visual da sidebar (delta 2026-09-16, referência visual
// fornecida) — cada item ganha um rótulo `section` PURAMENTE para
// agrupamento visual (categorias/separadores da nova sidebar escura).
// Nenhuma rota/permissão/feature flag foi tocada: são os MESMOS objetos
// já retornados antes, só com um campo novo. A visibilidade de cada item
// continua 100% decidida pelas condições abaixo (role/hasFeature/blocked),
// exatamente como já era.
const NAV_SECTION_OPERACAO = "Operação";
const NAV_SECTION_FINANCEIRO = "Financeiro";
const NAV_SECTION_CADASTROS = "Cadastros";
const NAV_SECTION_ADMINISTRACAO = "Administração";

const buildNav = (role, blocked, temPendencia, hasFeature) => {
  // Pacote prioritário, item 7/8 — ⚠ ao lado de "Financeiro" quando há
  // QUALQUER cobrança pendente (em_aberto, vencida ou não), mesmo que o
  // tenant ainda não esteja bloqueado (1-14 dias, ou liberado por
  // confiança). Nunca confundir alerta de pendência com bloqueio.
  const financeiroItem = { to: "/financeiro", label: "Financeiro", icon: Wallet, end: false, alert: temPendencia, section: NAV_SECTION_FINANCEIRO };
  if (blocked) return [financeiroItem];
  const base = [];
  if (role !== "analista") base.push({ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, section: NAV_SECTION_OPERACAO });
  base.push({ to: "/clientes", label: "Clientes", icon: Users, section: NAV_SECTION_CADASTROS });
  // Reorganização do menu (delta 2026-09-16, referência visual fornecida)
  // — "Usuários e permissões"/"Tipos de serviço"/"Grupos"/"Produtos-
  // Sistemas" saem de dentro de Configurações (aba "Operação", removida
  // de lá — ver Configuracoes.jsx) e passam a ser links diretos aqui em
  // Cadastros. MESMA rota (/configuracoes), MESMA tela/aba/lógica/
  // permissão de sempre — só com ?tab= para abrir direto na aba certa
  // (Configuracoes.jsx lê esse parâmetro só na carga inicial). Nenhuma
  // rota nova, nenhum módulo novo.
  base.push({ to: "/configuracoes?tab=usuarios", label: "Usuários e permissões", icon: UserCog, section: NAV_SECTION_CADASTROS, matchTab: "usuarios" });
  base.push({ to: "/configuracoes?tab=tipos", label: "Tipos de serviço", icon: Tag, section: NAV_SECTION_CADASTROS, matchTab: "tipos" });
  base.push({ to: "/configuracoes?tab=grupos", label: "Grupos", icon: UsersRound, section: NAV_SECTION_CADASTROS, matchTab: "grupos" });
  base.push({ to: "/configuracoes?tab=produtos", label: "Produtos/Sistemas", icon: Boxes, section: NAV_SECTION_CADASTROS, matchTab: "produtos" });
  base.push({ to: "/fichas", label: "Fichas de Serviço", icon: FileText, section: NAV_SECTION_OPERACAO });
  if (role === "admin" || role === "supervisor") {
    base.push({ to: "/aprovacoes", label: "Aprovações", icon: ClipboardCheck, section: NAV_SECTION_OPERACAO });
    base.push({ to: "/faturamento", label: "Faturamento & Cobrança", icon: Receipt, section: NAV_SECTION_FINANCEIRO });
    // NFS-e Nacional, Fase 1 — preparação/homologação (Produção Restrita),
    // NUNCA emissão com validade jurídica ainda (ver docs/NFSE_NACIONAL.md).
    // NFS-e como recurso comercial (delta 2026-09-15) — item recém-vendável
    // separadamente do plano principal; só aparece quando a empresa tem o
    // recurso "emissao_nfse" contratado (concedido pelo Admin Master em
    // Planos e Recursos). O backend é a barreira real (require_feature nos
    // endpoints de NFS-e) — isto só evita mostrar um link que levaria a uma
    // tela bloqueada pelo ProtectedRoute.
    if (hasFeature("emissao_nfse")) base.push({ to: "/notas-fiscais", label: "Notas Fiscais", icon: FileBadge2, section: NAV_SECTION_FINANCEIRO });
    base.push({ to: "/reconciliacao", label: "Reconciliação", icon: Banknote, section: NAV_SECTION_FINANCEIRO });
  }
  // Financeiro (Prioridades 10/11) — mensalidade LOOTUS da PRÓPRIA empresa
  // junto à Altaforja, sempre visível para qualquer perfil do tenant
  // (nunca só admin: mesmo bloqueado, quem estiver logado precisa
  // conseguir chegar aqui para regularizar o acesso).
  base.push(financeiroItem);
  base.push({ to: "/relatorios", label: "Relatórios", icon: BarChart3, section: NAV_SECTION_FINANCEIRO });
  base.push({ to: "/configuracoes", label: "Configurações", icon: Settings, section: NAV_SECTION_ADMINISTRACAO });
  return base;
};

export default function Layout() {
  const { user, logout, hasFeature } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  // Pacote consolidado pós-homologação, Prioridade 12 (e pacote prioritário,
  // itens 7/9/30) — alerta financeiro discreto e persistente, visível em
  // QUALQUER tela do tenant (não só dentro do próprio Financeiro), sempre
  // com link direto para lá. Prioridade: vencida > disponível. Nunca um
  // popup — uma faixa fina no topo, sem interromper o uso do restante do
  // sistema. Quando a empresa está bloqueada, ProtectedRoute já força a
  // navegação para /financeiro, cuja própria tela mostra o modal completo
  // — esta faixa fica reservada para os estados NÃO bloqueantes. Reaproveita
  // /financeiro/status (já calculado no backend — nunca replica o cálculo
  // financeiro aqui, item 30 do pedido) para os dois usos: a faixa E o ⚠ do
  // menu lateral (tem_pendencia é mais amplo que "bloqueado" — existe
  // mesmo em 1-14 dias de atraso ou com confiança liberada).
  const [finAlert, setFinAlert] = useState(null);
  const [diasAtraso, setDiasAtraso] = useState(0);
  const [temPendencia, setTemPendencia] = useState(false);
  useEffect(() => {
    if (!user?.company_id) return;
    api.get("/financeiro/status").then(({ data }) => {
      setTemPendencia(!!data.tem_pendencia);
      setDiasAtraso(data.dias_atraso || 0);
      if (data.bloqueado) return;
      if (data.cobranca_vencida) setFinAlert("vencida");
      else if (data.cobranca_disponivel) setFinAlert("disponivel");
    }).catch(() => {});
  }, [user?.company_id]);

  const nav = buildNav(user?.role, user?.financially_blocked, temPendencia, hasFeature);
  // Agrupamento puramente visual dos MESMOS itens de `nav` (nenhum item
  // novo, nenhuma condição de visibilidade nova) — ordem fixa de seções;
  // item sem `section` reconhecida (não deveria acontecer, defensivo)
  // renderiza sem cabeçalho de categoria, nunca é omitido.
  const NAV_SECTIONS_ORDER = [NAV_SECTION_OPERACAO, NAV_SECTION_FINANCEIRO, NAV_SECTION_CADASTROS, NAV_SECTION_ADMINISTRACAO];
  const navGroups = NAV_SECTIONS_ORDER
    .map(section => ({ section, items: nav.filter(i => i.section === section) }))
    .filter(g => g.items.length > 0);
  const navUngrouped = nav.filter(i => !NAV_SECTIONS_ORDER.includes(i.section));
  // Vários itens agora apontam para a MESMA rota /configuracoes (só o
  // parâmetro ?tab= muda — ver comentário em buildNav) — o isActive
  // padrão do NavLink só compara pathname, então "Configurações" e os 4
  // itens de Cadastros movidos ficariam TODOS marcados como ativos ao
  // mesmo tempo. `matchTab` (presente só nesses itens) decide qual deles
  // é o ativo de verdade, comparando com o ?tab= da URL atual.
  const currentConfigTab = new URLSearchParams(location.search).get("tab") || "";
  // Redução de espaçamento vertical (delta 2026-09-18) — só no DESKTOP
  // (prefixo `md:`), nunca no mobile: `renderNavItem`/`renderNavGroups`
  // geram o MESMO markup para os dois (a sidebar fixa do desktop chama
  // `renderNavGroups()`, o drawer mobile chama `renderNavGroups(...)`
  // logo abaixo) — sem o prefixo, qualquer ajuste de padding/gap aqui
  // afetaria os dois. Como o drawer mobile só é visível abaixo do
  // breakpoint `md` (a classe `md:hidden` no container do drawer) e a
  // sidebar fixa só é visível a partir dele (`hidden md:flex`), uma
  // classe `md:`-prefixada some no mobile e só entra em vigor no
  // desktop — exatamente o efeito de "só a sidebar do tenant no
  // desktop", sem duplicar o componente.
  const renderNavItem = ({ to, label, icon: Icon, end, alert, matchTab }, onClick) => {
    const isConfigRoute = to.startsWith("/configuracoes");
    const activeOverride = isConfigRoute ? (location.pathname === "/configuracoes" && currentConfigTab === (matchTab || "")) : null;
    return (
      <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-lg text-[14px] text-slate-300 ${(activeOverride ?? isActive) ? "active" : ""}`}>
        <Icon size={18} />
        <span>{label}</span>
        {alert && (
          <AlertTriangle size={14} className="text-amber-400 ml-auto" data-testid={`nav-alerta-${label.toLowerCase()}`} title="Existem cobranças pendentes." />
        )}
      </NavLink>
    );
  };
  const renderNavGroups = (onClick) => (
    <>
      {navGroups.map(({ section, items }) => (
        <div key={section}>
          <div className="px-3 pb-1 md:pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{section}</div>
          <div className="space-y-1 md:space-y-0.5">{items.map(item => renderNavItem(item, onClick))}</div>
        </div>
      ))}
      {navUngrouped.map(item => renderNavItem(item, onClick))}
    </>
  );

  // White-label (Pacote 2, item 1) — identidade visual do TENANT autenticado,
  // nunca escolhida pelo frontend: /branding deriva o company_id do próprio
  // token (ver GET /branding em server.py). "nunca apresentar logo quebrada"
  // -> logoError derruba para o fallback (inicial da empresa) se a URL/base64
  // falhar ao carregar.
  const [branding, setBranding] = useState(null);
  const [logoError, setLogoError] = useState(false);
  useEffect(() => {
    api.get("/branding").then(r => { setBranding(r.data); applyBrandingVars(r.data); }).catch(() => {});
  }, []);
  useEffect(() => { setLogoError(false); }, [branding?.logo_url]);
  const brandName = displayName(branding);

  // Tema Claro/Escuro (Pacote de Correções, item 4) — preferência do
  // usuário, independente da Personalização do tenant acima (ver
  // lib/theme.js para a decisão de mecanismo/persistência).
  const [theme, setTheme] = useState(getInitialTheme);
  useEffect(() => { applyTheme(theme); }, [theme]);
  const toggleTheme = () => setTheme(t => (t === "dark" ? "light" : "dark"));

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop — experimento visual (delta 2026-09-16, referência
          visual fornecida): fundo azul-marinho profundo com leve degradê,
          item ativo em cápsula arredondada na cor de marca do tenant
          (--brand-primary, já existente — nenhuma cor nova hardcoded).
          Logo/marca do tenant preservados sem nenhuma alteração.
          Correção de rolagem vertical (delta 2026-09-18) — o container pai
          é `min-h-screen flex`; `min-height` só define um PISO, nunca um
          teto, então em página longa (ex.: Dashboard) o container cresce
          além de 100vh e, como flex item sem altura própria, o <aside>
          esticava junto (align-items: stretch padrão) — o fundo escuro
          acompanhava o documento inteiro, não só a viewport. `h-screen`
          fixa a altura em 100vh (nunca mais estica com o conteúdo do
          <main> ao lado); `sticky top-0 self-start` mantém o menu preso
          no topo da viewport durante a rolagem da página (a rolagem
          continua acontecendo na janela — não é um segundo scroll
          independente). O <nav> logo abaixo já era `flex-1
          overflow-y-auto`: com o <aside> agora limitado a 100vh, isso
          passa a valer de verdade — itens que não cabem rolam só dentro
          do menu, sem empurrar o rodapé (Novidades/Ajuda/Powered by)
          para fora da tela. */}
      <aside className="hidden md:flex md:w-[260px] md:h-screen md:sticky md:top-0 md:self-start flex-col bg-gradient-to-b from-slate-950 to-slate-900 border-r border-slate-800/70">
        <div className="h-16 flex items-center px-6 border-b border-slate-800/70 gap-3">
          {branding?.logo_url && !logoError ? (
            <img src={branding.logo_url} alt={brandName} className="w-9 h-9 rounded-lg object-contain bg-white border border-slate-100 shrink-0" onError={() => setLogoError(true)} />
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-heading font-black shrink-0" style={{ background: "linear-gradient(135deg, hsl(var(--brand-primary)), hsl(var(--brand-accent)))" }}>{initials(brandName)}</div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-black leading-tight text-[16px] tracking-tight truncate text-white" title={brandName}>{brandName}</div>
            {/* Pacote de Correções, item 2 — segunda linha é o Slogan/
                Subtítulo configurado pelo tenant (opcional). Sem texto fixo
                substituto quando vazio: "GESTÃO DE SERVIÇOS" pertencia à
                identidade do LOOTUS, não deve aparecer em ambiente
                white-label sem que a empresa tenha escolhido um texto. */}
            {branding?.slogan && (
              <div className="text-[11px] text-slate-400 leading-tight truncate" title={branding.slogan}>{branding.slogan}</div>
            )}
          </div>
        </div>
        {/* Espaçamento vertical (delta 2026-09-18) — `space-y-4`→
            `space-y-3` (gap ENTRE grupos/títulos OPERAÇÃO/FINANCEIRO/
            CADASTROS/ADMINISTRAÇÃO) só existe aqui (o drawer mobile logo
            abaixo tem seu próprio `space-y-4` num elemento separado —
            não precisa de `md:`). `no-scrollbar` esconde a BARRA visual
            (ver index.css) só como rede de segurança para viewport muito
            baixa — `overflow-y-auto` continua ativo, então o menu
            continua rolável por mouse/touchpad/teclado; nenhum item
            fica inacessível, só o trilho cinza some. */}
        <nav className="p-3 flex-1 space-y-3 overflow-y-auto no-scrollbar" data-testid="sidebar-nav">
          {renderNavGroups()}
        </nav>
        {/* Rodapé institucional (Pacote 2, item 11).
            "Sobre" removido do menu (delta 2026-09-18) — só informações
            do sistema, sem necessidade de ficar visível no menu do
            cliente; ajuda a caber sem scroll interno na viewport normal
            agora que a sidebar está travada em 100vh. Rota/componente
            (App.js: /sobre -> pages/Sobre.jsx) preservados intactos — só
            o link de navegação saiu, a página continua acessível
            diretamente pela URL. Padding/itens levemente mais compactos
            (delta 2026-09-18, só aqui — bloco próprio, não compartilhado
            com o rodapé do drawer mobile logo abaixo, que continua como
            estava). */}
        <div className="border-t border-slate-800/70 p-2.5 space-y-0.5">
          <NavLink to="/novidades" data-testid="nav-novidades" className={({ isActive }) => `side-link flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] text-slate-400 ${isActive ? "active" : ""}`}>
            <Sparkles size={15} /> Novidades da versão
          </NavLink>
          <NavLink to="/ajuda" data-testid="nav-ajuda" className={({ isActive }) => `side-link flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] text-slate-400 ${isActive ? "active" : ""}`}>
            <HelpCircle size={15} /> Ajuda
          </NavLink>
          <div className="px-3 pt-1.5 text-[11px] text-slate-500">
            Powered by <b className="text-primary">{PLATFORM_COMPANY_NAME}</b>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar — mesma remoção de "Sobre" do rodapé (item acima),
          para os dois nunca ficarem inconsistentes entre si. */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-gradient-to-b from-slate-950 to-slate-900 border-r border-slate-800/70 p-3 overflow-y-auto">
            <div className="flex items-center justify-between px-2 py-3">
              <div className="font-heading font-bold text-white">Menu</div>
              <button data-testid="sidebar-close" className="text-slate-300" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="space-y-4">{renderNavGroups(() => setOpen(false))}</div>
            <div className="border-t border-slate-800/70 mt-3 pt-2 space-y-0.5">
              <NavLink to="/novidades" onClick={() => setOpen(false)} className={({ isActive }) => `side-link flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-slate-400 ${isActive ? "active" : ""}`}><Sparkles size={15} /> Novidades da versão</NavLink>
              <NavLink to="/ajuda" onClick={() => setOpen(false)} className={({ isActive }) => `side-link flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-slate-400 ${isActive ? "active" : ""}`}><HelpCircle size={15} /> Ajuda</NavLink>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Cabeçalho/barra superior (Correções, item 3: "também deverá fazer
            parte da personalização") — fundo/texto seguem o tema
            claro/escuro (bg-card), e a borda inferior recebe um traço na
            cor principal do tenant, mesmo mecanismo de --brand-primary já
            usado no menu lateral. */}
        <header className="h-16 bg-card flex items-center justify-between px-4 md:px-6 border-b-2" style={{ borderBottomColor: "hsl(var(--brand-primary) / 0.35)" }}>
          <button data-testid="sidebar-open" className="md:hidden p-2" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="md:hidden font-heading font-bold">Relatório</div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle"
              title={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-3 hover:bg-slate-100 rounded-md px-2 py-1.5 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-heading font-semibold">
                    {(user?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium leading-tight">{user?.name}</div>
                    <div className="text-xs text-slate-500 leading-tight capitalize">{user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-change-password" onClick={() => setPwOpen(true)}>
                  <KeyRound size={16} /> Alterar senha
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={doLogout}>
                  <LogOut size={16} /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {finAlert && location.pathname !== "/financeiro" && (
          <div
            className={`px-4 md:px-8 py-2 text-sm flex items-center justify-between gap-3 ${finAlert === "vencida" ? "bg-amber-50 text-amber-900 border-b border-amber-200" : "bg-slate-50 text-slate-700 border-b border-slate-200"}`}
            data-testid="financeiro-alerta-topo"
          >
            <span>
              {finAlert === "vencida"
                ? alertaVencidaTexto(diasAtraso)
                : "Sua mensalidade LOOTUS deste mês já está disponível. Acesse Financeiro para pagar por Boleto ou PIX."}
            </span>
            <NavLink to="/financeiro" className="font-medium underline shrink-0">Ir para Financeiro</NavLink>
          </div>
        )}
        <main className="p-4 md:p-8 flex-1 fade-in">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
